import { Space, Tag } from 'antd'
import { ReloadOutlined, ShoppingOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { onGetListOrders } from 'src/redux/order/order.action'
import {
	selectCurrentPageListOrders,
	selectListOrders,
	selectTotalOrders,
} from 'src/redux/order/order.selector'
import { DEFAULT_PAGE_SIZE, ORDER_STATUS } from 'src/utils/constant'
import {
	ButtonCommon,
	ListPageIntro,
	ListPageShell,
	ListPageTotal,
	PagedResourceList,
	PopoverCommon,
} from '../../common'
import { useAppDispatch, useAppSelector } from '../../hooks'
import {
	formatDateTime,
	formatMoney,
	getUserDisplayName,
} from '../../utils/function.utils'
import FormAddOrder from './components/FormAddOrder/FormAddOrder'
import { OrderAction } from './components/OrderAction/OrderAction'
import './OrderList.scss'

const renderOrderStatus = status => {
	let color = 'orange'
	switch (status) {
		case ORDER_STATUS.PENDING:
			color = 'orange'
			break
		case ORDER_STATUS.SUCCESS:
			color = 'green'
			break

		case ORDER_STATUS.FAILED:
			color = 'red'
			break

		default:
			color = 'cyan'
	}

	return <Tag color={color}>{status}</Tag>
}

export const OrderList = () => {
	const dispatch = useAppDispatch()
	const data = useAppSelector(selectListOrders)
	const total = useAppSelector(selectTotalOrders)
	const currentPage = useAppSelector(selectCurrentPageListOrders)

	const [isReloadLoading, setIsReloadLoading] = useState()
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

	const resource = useMemo(
		() => ({ data, total, page: currentPage }),
		[data, total, currentPage]
	)

	const fetchPage = useCallback(
		(page = 0, cb) => dispatch(onGetListOrders({ pageNum: page, pageSize }, cb)),
		[dispatch, pageSize]
	)

	useEffect(() => {
		dispatch(onGetListOrders({ pageNum: 0, pageSize }))
	}, [dispatch, pageSize])

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 72,
		},
		{
			title: 'Code',
			dataIndex: 'code',
			key: 'code',
		},
		{
			title: 'Transaction ID',
			dataIndex: 'transactionID',
			key: 'transactionID',
		},
		{
			title: 'User name',
			dataIndex: 'user',
			key: 'userName',
			render: user => (
				<div className="order-list__user-cell">
					<div>{getUserDisplayName(user)}</div>
					<div>{user?.authName}</div>
					<div>{user?.email}</div>
				</div>
			),
		},

		{
			title: 'Price',
			dataIndex: 'amount',
			key: 'amount',
			render: value => formatMoney(value),
		},
		{
			title: 'Subscription code',
			dataIndex: 'subscriptionCode',
			key: 'subscriptionCode',
		},
		{
			title: 'Method',
			dataIndex: 'method',
			key: 'method',
		},
		{
			title: 'Status',
			dataIndex: 'orderStatus',
			key: 'orderStatus',
			render: status => renderOrderStatus(status),
		},
		{
			title: 'Created at',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: value => formatDateTime(value),
		},
		{
			title: 'Updated at',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			render: value => formatDateTime(value),
		},
		{
			title: 'Created by',
			dataIndex: 'createdBy',
			key: 'createdBy',
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			width: 120,
			align: 'center',
			// eslint-disable-next-line react/display-name
			render: (_, record) => {
				return (
					<OrderAction
						id={record.id}
						orderStatus={record.orderStatus}
						userID={record.userID || record.user?.id}
						subscriptionCode={record.subscriptionCode}
						method={record.method}
						transactionID={record.transactionID}
					/>
				)
			},
		},
	]

	const handleReload = () => {
		setIsReloadLoading(true)
		dispatch(
			onGetListOrders({ pageNum: 0, pageSize }, () => {
				setIsReloadLoading(false)
			})
		)
	}

	const getRowKey = record => {
		return record.id
	}

	return (
		<ListPageShell>
			<PagedResourceList
				resource={resource}
				columns={columns}
				fetchPage={fetchPage}
				rowKey={getRowKey}
				pageSize={pageSize}
				onPageSizeChange={setPageSize}
				fetchOnMount={false}
				header={
					<div className="list-page-bar">
						<ListPageIntro
							icon={ShoppingOutlined}
							title="Orders"
							subtitle="Subscription and payment orders. Create a new order or reload to refresh."
						/>
						<div className="list-page__aside">
							<div className="list-page__aside-row">
								<ListPageTotal total={total} />
								<Space wrap>
									<ButtonCommon
										onClick={handleReload}
										loading={isReloadLoading}
										type="primary"
										icon={<ReloadOutlined />}
									>
										Reload
									</ButtonCommon>
									<PopoverCommon
										content={<FormAddOrder />}
										trigger="click"
									>
										<ButtonCommon ghost type="primary">
											+ Create new order
										</ButtonCommon>
									</PopoverCommon>
								</Space>
							</div>
						</div>
					</div>
				}
				tableProps={{ className: 'table-common--fluid' }}
			/>
		</ListPageShell>
	)
}
